import { useEffect, useState } from "react";
import { api, type MembershipRow } from "../api.js";
import { useLocale } from "../ui.js";

export function MembershipPanel({ token }: { token: string }) {
  const { tr } = useLocale();
  const [rows, setRows] = useState<MembershipRow[] | null>(null);
  const [selected, setSelected] = useState<MembershipRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  function load() {
    void api.adminMemberships(token).then((res) => setRows(res.ok && res.data ? res.data.memberships : []));
  }

  useEffect(() => {
    load();
  }, [token]);

  async function save() {
    if (!selected) return;
    setBusy(true);
    setSaved(false);
    const res = await api.adminSaveMembership(token, selected);
    setBusy(false);
    if (res.ok) {
      setSaved(true);
      load();
    }
  }

  function field<K extends keyof MembershipRow>(key: K, value: MembershipRow[K]) {
    setSelected((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSaved(false);
  }

  return (
    <section className="panel-card">
      <h2>{tr("memberships")}</h2>
      <p className="section-desc">{tr("owner")} · trial / license</p>
      {rows === null ? (
        <p className="muted">{tr("loading")}</p>
      ) : rows.length === 0 ? (
        <p className="muted">{tr("emptyList")}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{tr("email")}</th>
              <th>Name</th>
              <th>License</th>
              <th>Trial</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.email}>
                <td>{m.email}</td>
                <td>{m.displayName ?? m.personName ?? "—"}</td>
                <td>
                  {m.licenseStatus} ({m.activeLicenses})
                </td>
                <td>{m.trial ? "yes" : "no"}</td>
                <td>
                  <button className="btn-ghost" onClick={() => setSelected(m)}>
                    edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected ? (
        <div className="panel-card" style={{ marginTop: 18 }}>
          <h2>{selected.email}</h2>
          <div className="grid-2">
            <div>
              <label>Display name</label>
              <input value={selected.displayName ?? ""} onChange={(e) => field("displayName", e.target.value || null)} />
            </div>
            <div>
              <label>Mobile</label>
              <input value={selected.mobile ?? ""} onChange={(e) => field("mobile", e.target.value || null)} />
            </div>
            <div>
              <label>License status</label>
              <input value={selected.licenseStatus} onChange={(e) => field("licenseStatus", e.target.value)} />
            </div>
            <div>
              <label>Active licenses</label>
              <input
                type="number"
                min={0}
                value={selected.activeLicenses}
                onChange={(e) => field("activeLicenses", Number(e.target.value) || 0)}
              />
            </div>
          </div>
          <label className="switch" style={{ marginTop: 12 }}>
            <input type="checkbox" checked={selected.trial} onChange={(e) => field("trial", e.target.checked)} />
            Trial active
          </label>
          {saved ? <div className="msg ok">{tr("saved")}</div> : null}
          <div className="btn-row">
            <button className="btn-ghost" onClick={() => setSelected(null)}>
              close
            </button>
            <button className="btn-primary" style={{ width: "auto" }} disabled={busy} onClick={() => void save()}>
              {tr("save")}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
